import { v4 as uuidv4 } from 'uuid';
import {
  WL_ExpressionTypes,
  WL_WorkflowNodeType,
  type WorkflowCanvasEdge,
  type WorkflowCanvasNode,
  type WorkflowStartMeta,
} from './workflow.types';
import type {
  WorkflowConditionsSaveDto,
  WorkflowConnectorResponseDto,
  WorkflowExpressionConditionResponseItemDto,
  WorkflowExpressionConditionSaveItemDto,
  WorkflowGraphResponseDto,
  WorkflowGraphSaveRequestDto,
  WorkflowLlmConditionResponseItemDto,
  WorkflowLlmConditionSaveItemDto,
  WorkflowNodeResponseDto,
  WorkflowNodeSaveItemDto,
  WorkflowNodeTypeDto,
} from '../../../../../../core/models/chatbot-workflow-graph.model';

/** Every canvas node gets a new UUID; never derived from DB `pid`. */
export function newCanvasNodeId(): string {
  return uuidv4();
}

function toSystemPromptUi(s: string | null | undefined): string {
  return s === null || s === undefined ? '' : s;
}

function nodeRowToCanvas(n: WorkflowNodeResponseDto, canvasId: string): WorkflowCanvasNode {
  const workflow = {
    pid: n.pid,
    chatbotId: n.chatbotId,
    nodeType: n.nodeType as WL_WorkflowNodeType,
    name: n.name,
    systemPrompt: toSystemPromptUi(n.systemPrompt),
    knowledgeBaseId: n.knowledgeBaseId ?? undefined,
    overrideSystemPrompt: n.overrideSystemPrompt,
    modelId: n.modelId ?? undefined,
    llmConditionId: n.llmConditionId ?? undefined,
    expressionId: n.expressionId ?? undefined,
    isStartNode: n.isStartNode,
    ...(n.toolIds !== undefined ? { toolIds: [...n.toolIds] } : {}),
    scheduleDraft: n.schedule
      ? {
          pid: n.schedule.pid ?? null,
          name: n.schedule.name,
          triggerPrompt: n.schedule.triggerPrompt,
          cronExpr: n.schedule.cronExpr,
          timezone: n.schedule.timezone,
          isActive: n.schedule.enabled,
          maxRetries: n.schedule.maxRetries ?? 0,
          retryIntervalMins: n.schedule.retryIntervalMins ?? 5,
          preflightCheckSql: n.schedule.preflightCheckSql ?? undefined,
          preflightMinRows: n.schedule.preflightMinRows ?? 1,
          upstreamWorkflowIds: n.schedule.upstreamWorkflowIds ?? [],
        }
      : undefined,
  };
  if (n.isStartNode) {
    const startMeta: WorkflowStartMeta = {
      pid: n.pid,
      chatbotId: n.chatbotId,
      name: n.name,
      systemPrompt: n.systemPrompt ?? null,
    };
    return {
      id: canvasId,
      kind: 'START',
      x: n.positionX,
      y: n.positionY,
      startMeta,
      workflow,
    };
  }
  return {
    id: canvasId,
    kind: 'WORKFLOW',
    x: n.positionX,
    y: n.positionY,
    workflow,
  };
}

function coerceExpressionOperator(s: string): WL_ExpressionTypes {
  const v = (Object.values(WL_ExpressionTypes) as string[]).includes(s)
    ? (s as WL_ExpressionTypes)
    : null;
  return v ?? WL_ExpressionTypes.EQUALS;
}

/**
 * Apply GET `conditions` to forward edges: each target workflow `pid` maps to at most one
 * LLM row and/or one expression row; expression wins when both exist (invalid server state).
 */
function hydrateEdgesFromConditions(
  edges: WorkflowCanvasEdge[],
  nodes: WorkflowCanvasNode[],
  conditions: WorkflowGraphResponseDto['conditions']
): void {
  if (!conditions) return;

  const llmByTargetPid = new Map<number, WorkflowLlmConditionResponseItemDto>();
  for (const c of conditions.llmConditions ?? []) {
    llmByTargetPid.set(c.pid, c);
  }
  const exprByTargetPid = new Map<number, WorkflowExpressionConditionResponseItemDto>();
  for (const c of conditions.expressionConditions ?? []) {
    exprByTargetPid.set(c.pid, c);
  }

  for (const e of edges) {
    const child = nodes.find((n) => n.id === e.toNodeId);
    const w = child?.workflow;
    const targetPid = w?.pid;
    if (child?.kind !== 'WORKFLOW' || w == null || targetPid == null) continue;

    const expr = exprByTargetPid.get(targetPid);
    const llm = llmByTargetPid.get(targetPid);

    if (expr) {
      e.transitionType = 'EXPRESSION';
      e.expressionConditionRowId = expr.conditionId;
      e.llmConditionRowId = undefined;
      e.expressionOperatorDraft = coerceExpressionOperator(expr.expressionType);
      e.expressionVariableDraft = expr.sourceVariable;
      e.expressionValueDraft = expr.targetVariable;
      const lbl = expr.label != null && String(expr.label).trim() !== '' ? String(expr.label) : '';
      e.label = lbl || w.name || e.label;
    } else if (llm) {
      e.transitionType = 'LLM';
      e.llmConditionRowId = llm.conditionId;
      e.expressionConditionRowId = undefined;
      e.llmConditionDraft = llm.conditionPrompt;
      e.label = llm.label || llm.conditionPrompt || w.name || e.label;
    }
  }
}

/** Map GET payload → canvas nodes + edges (supports multiple incoming edges per node). */
export function workflowGraphResponseToCanvas(
  dto: WorkflowGraphResponseDto,
  existingNodes: WorkflowCanvasNode[] = []
): { nodes: WorkflowCanvasNode[]; edges: WorkflowCanvasEdge[] } {
  const pidToCanvasId = new Map<number, string>();
  for (const row of dto.nodes) {
    // 1. Try to find existing node by PID
    let existing = existingNodes.find((n) => n.workflow?.pid === row.pid);

    // 2. Start node match (if PID not yet assigned or different)
    if (!existing && row.isStartNode) {
      existing = existingNodes.find((n) => n.kind === 'START');
    }

    // 3. Match by clientKey (returned for newly created nodes)
    if (!existing && (row as any).clientKey) {
      existing = existingNodes.find((n) => n.workflow?.clientKey === (row as any).clientKey);
    }

    // 4. Fallback: Match by position and name for new nodes without clientKey in response
    if (!existing) {
      existing = existingNodes.find(
        (n) =>
          !n.workflow?.pid &&
          n.x === row.positionX &&
          n.y === row.positionY &&
          (n.workflow?.name === row.name || n.startMeta?.name === row.name)
      );
    }

    pidToCanvasId.set(row.pid, existing ? existing.id : newCanvasNodeId());
  }
  const nodes = dto.nodes.map((row) =>
    nodeRowToCanvas(row, pidToCanvasId.get(row.pid)!)
  );
  const edges: WorkflowCanvasEdge[] = dto.connectors.map((c: WorkflowConnectorResponseDto) => {
    const fromId = pidToCanvasId.get(c.fromNodeId);
    const toId = pidToCanvasId.get(c.toNodeId);
    if (!fromId || !toId) {
      throw new Error(
        `workflowGraphResponseToCanvas: unknown connector endpoint (from ${c.fromNodeId} to ${c.toNodeId})`
      );
    }
    return {
      id: `conn-${c.pid}`,
      fromNodeId: fromId,
      toNodeId: toId,
      connectorPid: c.pid,
    };
  });
  assignOutgoingEdgeSortOrders(edges);
  hydrateEdgesFromConditions(edges, nodes, dto.conditions);
  return { nodes, edges };
}

/** One evaluation order per parent: preserve connector list order from the API. */
function assignOutgoingEdgeSortOrders(edges: WorkflowCanvasEdge[]): void {
  const byFrom = new Map<string, WorkflowCanvasEdge[]>();
  for (const e of edges) {
    const arr = byFrom.get(e.fromNodeId) ?? [];
    arr.push(e);
    byFrom.set(e.fromNodeId, arr);
  }
  for (const arr of byFrom.values()) {
    arr.forEach((e, i) => {
      if (e.sortOrder === undefined) e.sortOrder = i;
    });
  }
}

function resolveEndpoint(
  canvasNodeId: string,
  nodeList: WorkflowCanvasNode[]
): { nodeId?: number; clientKey?: string } {
  const n = nodeList.find((x) => x.id === canvasNodeId);
  const w = n?.workflow;
  if (!w) return {};
  if (w.pid != null) return { nodeId: w.pid };
  if (w.clientKey) return { clientKey: w.clientKey };
  return {};
}

function startNodeSaveFields(n: WorkflowCanvasNode): {
  name: string;
  systemPrompt: string | null;
} {
  const sm = n.startMeta;
  const w = n.workflow;
  return {
    name: sm?.name ?? w?.name ?? 'Start',
    systemPrompt:
      sm?.systemPrompt !== undefined
        ? sm.systemPrompt
        : w?.systemPrompt === '' || w?.systemPrompt === undefined
          ? null
          : (w.systemPrompt ?? null),
  };
}

/** Serialize canvas → API save body (`chatbotId` comes from the PUT URL only). */
export function canvasToWorkflowGraphSaveRequest(
  nodes: WorkflowCanvasNode[],
  edges: WorkflowCanvasEdge[]
): WorkflowGraphSaveRequestDto {
  const saveNodes: WorkflowNodeSaveItemDto[] = nodes
    .map((n): WorkflowNodeSaveItemDto | null => {
      if (n.kind === 'START') {
        const { name, systemPrompt } = startNodeSaveFields(n);
        const w = n.workflow;
        if (!w) return null;
        const base: WorkflowNodeSaveItemDto = {
          name,
          nodeType: (w.nodeType ?? WL_WorkflowNodeType.AGENT) as WorkflowNodeTypeDto,
          systemPrompt,
          knowledgeBaseId: w.knowledgeBaseId ?? null,
          overrideSystemPrompt: w.overrideSystemPrompt ?? false,
          modelId: w.modelId ?? null,
          llmConditionId: w.llmConditionId ?? null,
          expressionId: w.expressionId ?? null,
          isStartNode: true,
          positionX: n.x,
          positionY: n.y,
          ...(w.toolIds !== undefined ? { toolIds: [...w.toolIds] } : {}),
          schedule: w.scheduleDraft
            ? {
                pid: w.scheduleDraft.pid ?? undefined,
                name: w.scheduleDraft.name,
                triggerPrompt: w.scheduleDraft.triggerPrompt,
                cronExpr: w.scheduleDraft.cronExpr,
                timezone: w.scheduleDraft.timezone,
                enabled: w.scheduleDraft.isActive,
                maxRetries: w.scheduleDraft.maxRetries,
                retryIntervalMins: w.scheduleDraft.retryIntervalMins,
                preflightCheckSql: w.scheduleDraft.preflightCheckSql ?? undefined,
                preflightMinRows: w.scheduleDraft.preflightMinRows,
                upstreamWorkflowIds: w.scheduleDraft.upstreamWorkflowIds,
              }
            : undefined,
        };
        if (w.pid != null) {
          return { pid: w.pid, ...base };
        }
        if (!w.clientKey) {
          throw new Error('Start node has no pid and no clientKey');
        }
        return { clientKey: w.clientKey, ...base };
      }
      if (n.kind === 'WORKFLOW' && n.workflow) {
        const w = n.workflow;
        const base: WorkflowNodeSaveItemDto = {
          name: w.name ?? 'Agent',
          nodeType: (w.nodeType ?? WL_WorkflowNodeType.AGENT) as WorkflowNodeTypeDto,
          systemPrompt:
            w.systemPrompt === undefined || w.systemPrompt === ''
              ? null
              : w.systemPrompt,
          knowledgeBaseId: w.knowledgeBaseId ?? null,
          overrideSystemPrompt: w.overrideSystemPrompt ?? false,
          modelId: w.modelId ?? null,
          llmConditionId: w.llmConditionId ?? null,
          expressionId: w.expressionId ?? null,
          isStartNode: false,
          positionX: n.x,
          positionY: n.y,
          ...(w.toolIds !== undefined ? { toolIds: [...w.toolIds] } : {}),
          schedule: w.scheduleDraft
            ? {
                pid: w.scheduleDraft.pid ?? undefined,
                name: w.scheduleDraft.name,
                triggerPrompt: w.scheduleDraft.triggerPrompt,
                cronExpr: w.scheduleDraft.cronExpr,
                timezone: w.scheduleDraft.timezone,
                enabled: w.scheduleDraft.isActive,
                maxRetries: w.scheduleDraft.maxRetries,
                retryIntervalMins: w.scheduleDraft.retryIntervalMins,
                preflightCheckSql: w.scheduleDraft.preflightCheckSql ?? undefined,
                preflightMinRows: w.scheduleDraft.preflightMinRows,
                upstreamWorkflowIds: w.scheduleDraft.upstreamWorkflowIds,
              }
            : undefined,
        };
        if (w.pid != null) {
          return { pid: w.pid, ...base };
        }
        if (!w.clientKey) {
          throw new Error(`Workflow node ${n.id} has no pid and no clientKey`);
        }
        return { clientKey: w.clientKey, ...base };
      }
      return null;
    })
    .filter((x): x is WorkflowNodeSaveItemDto => x != null);

  const saveConnectors: WorkflowGraphSaveRequestDto['connectors'] = edges.map((e) => {
    const from = resolveEndpoint(e.fromNodeId, nodes);
    const to = resolveEndpoint(e.toNodeId, nodes);
    const row: WorkflowGraphSaveRequestDto['connectors'][number] = {};
    if (from.nodeId != null) row.fromNodeId = from.nodeId;
    else if (from.clientKey) row.fromClientKey = from.clientKey;
    else throw new Error(`Edge ${e.id}: unresolved from endpoint`);

    if (to.nodeId != null) row.toNodeId = to.nodeId;
    else if (to.clientKey) row.toClientKey = to.clientKey;
    else throw new Error(`Edge ${e.id}: unresolved to endpoint`);

    return row;
  });

  const conditions = buildWorkflowConditionsSaveDto(nodes, edges);

  return { nodes: saveNodes, connectors: saveConnectors, conditions };
}

/**
 * One condition per forward edge to a sub-agent: LLM vs expression comes from the edge transition type.
 * Child identity (`pid` or `clientKey`) is always the target sub-agent node.
 */
function buildWorkflowConditionsSaveDto(
  nodes: WorkflowCanvasNode[],
  edges: WorkflowCanvasEdge[]
): WorkflowConditionsSaveDto {
  const sorted = [...edges].sort((a, b) => {
    const byFrom = a.fromNodeId.localeCompare(b.fromNodeId);
    if (byFrom !== 0) return byFrom;
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  });

  const llmConditions: WorkflowLlmConditionSaveItemDto[] = [];
  const expressionConditions: WorkflowExpressionConditionSaveItemDto[] = [];
  /** API rejects duplicate targets in one request; multiple incoming edges share one row per child. */
  const seenLlmTargets = new Set<string>();
  const seenExprTargets = new Set<string>();

  for (const e of sorted) {
    const child = nodes.find((n) => n.id === e.toNodeId);
    if (!child || child.kind !== 'WORKFLOW' || !child.workflow) continue;

    const to = resolveEndpoint(e.toNodeId, nodes);
    const label =
      (e.label && e.label.trim()) ||
      child.workflow.name ||
      'Agent';

    if (to.nodeId == null && !to.clientKey) continue;

    const targetKey =
      to.nodeId != null ? `pid:${to.nodeId}` : `ck:${to.clientKey}`;

    const isExpression = e.transitionType === 'EXPRESSION';

    if (isExpression) {
      if (seenExprTargets.has(targetKey)) continue;
      seenExprTargets.add(targetKey);
      const row: WorkflowExpressionConditionSaveItemDto = {
        expressionType: e.expressionOperatorDraft ?? WL_ExpressionTypes.EQUALS,
        sourceVariable: e.expressionVariableDraft ?? '',
        targetVariable: e.expressionValueDraft ?? '',
      };
      const displayLabel =
        (e.label && e.label.trim()) || (child.workflow.name && child.workflow.name.trim()) || '';
      if (displayLabel) row.label = displayLabel;
      if (e.expressionConditionRowId != null) {
        row.conditionId = e.expressionConditionRowId;
      }
      if (to.nodeId != null) row.pid = to.nodeId;
      else row.clientKey = to.clientKey!;
      expressionConditions.push(row);
    } else if (e.transitionType === 'LLM') {
      if (seenLlmTargets.has(targetKey)) continue;
      seenLlmTargets.add(targetKey);
      const row: WorkflowLlmConditionSaveItemDto = {
        label,
        conditionPrompt: e.llmConditionDraft ?? '',
      };
      if (e.llmConditionRowId != null) {
        row.conditionId = e.llmConditionRowId;
      }
      if (to.nodeId != null) row.pid = to.nodeId;
      else row.clientKey = to.clientKey!;
      llmConditions.push(row);
    }
  }

  return { llmConditions, expressionConditions };
}

/**
 * Empty graph when GET fails or chatbot has no workflow rows yet.
 * Canvas ids are UUIDs; `pid` is null until the backend assigns one.
 */
export function emptyCanvasGraph(chatbotId: number): {
  nodes: WorkflowCanvasNode[];
  edges: WorkflowCanvasEdge[];
} {
  const id = newCanvasNodeId();
  const startMeta: WorkflowStartMeta = {
    pid: null,
    chatbotId,
    name: 'Start',
    systemPrompt: null,
  };
  return {
    nodes: [
      {
        id,
        kind: 'START',
        x: 120,
        y: 280,
        startMeta,
        workflow: {
          pid: null,
          clientKey: id,
          chatbotId,
          nodeType: WL_WorkflowNodeType.AGENT,
          name: 'Start',
          systemPrompt: '',
          overrideSystemPrompt: false,
          isStartNode: true,
        },
      },
    ],
    edges: [],
  };
}

/** Fresh client-side agent node (`pid` null until saved; `clientKey` matches canvas id). */
export function newAgentCanvasNode(
  chatbotId: number,
  x: number,
  y: number
): WorkflowCanvasNode {
  const id = newCanvasNodeId();
  return {
    id,
    kind: 'WORKFLOW',
    x,
    y,
    workflow: {
      pid: null,
      clientKey: id,
      chatbotId,
      nodeType: WL_WorkflowNodeType.AGENT,
      name: 'New agent',
      systemPrompt: '',
      overrideSystemPrompt: false,
      isStartNode: false,
    },
  };
}

/** Duplicate an agent node for canvas paste; new `id` / `clientKey`, no `pid` until saved. */
export function cloneAgentCanvasNode(
  source: WorkflowCanvasNode,
  x: number,
  y: number
): WorkflowCanvasNode {
  if (source.kind !== 'WORKFLOW' || !source.workflow) {
    throw new Error('cloneAgentCanvasNode: expected a workflow agent node');
  }
  const w = source.workflow;
  const id = newCanvasNodeId();
  return {
    id,
    kind: 'WORKFLOW',
    x: Math.round(x),
    y: Math.round(y),
    workflow: {
      pid: null,
      clientKey: id,
      chatbotId: w.chatbotId,
      nodeType: (w.nodeType ?? WL_WorkflowNodeType.AGENT) as WL_WorkflowNodeType,
      name: `${w.name ?? 'Agent'} (copy)`,
      systemPrompt: w.systemPrompt ?? '',
      overrideSystemPrompt: w.overrideSystemPrompt ?? false,
      modelId: w.modelId,
      providerId: w.providerId ?? null,
      knowledgeBaseId: w.knowledgeBaseId,
      isStartNode: false,
      ...(w.toolIds !== undefined ? { toolIds: [...w.toolIds] } : {}),
    },
  };
}
